import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { useResizeObserver } from 'usehooks-ts';

import { Modal, Table, Input, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined, EditOutlined } from '@ant-design/icons';

import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';
import { ICargoCarrier } from '@entities/cargo-carrier';
import { ICargoCarrierFilter } from '@entities/cargo-carrier-filter';

import { CargoCarrierType } from '@enums/cargo-carrier-type';
import { Permission, hasPermission } from '@enums/permission';

const filterContext: string = 'CargoCarriers';

const CargoCarriers = () => {
    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const initFilter: ICargoCarrierFilter = { type: CargoCarrierType.Truck };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<ICargoCarrierFilter>((s) => s.filters[filterContext]);

    const [cargoCarriers, setCargoCarriers] = useState<Array<ICargoCarrier>>([]);
    const [currentCargoCarrier, setCurrentCargoCarrier] = useState<ICargoCarrier>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setCargoCarriers([]);

        setLoading(true);

        serverFetch('cargocarriers', { method: 'GET', queryParams: filter })
            .then((data) => {
                setCargoCarriers(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения перевозчиков', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = () => {
        if (!currentCargoCarrier) return;

        serverFetch(`cargocarriers/${currentCargoCarrier.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления перевозчика', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 250 }}
                            key='searchText'
                            placeholder='Название / ИНН'
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCargoCarriers)) {
                                securityRestriction(api, [Permission.ManageCargoCarriers]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentCargoCarrier,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCargoCarriers)) {
                                securityRestriction(api, [Permission.ManageCargoCarriers]);
                                return;
                            }

                            navigate(`${resolved.pathname}/${currentCargoCarrier?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentCargoCarrier,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCargoCarrier)) {
                                securityRestriction(api, [Permission.DeleteCargoCarrier]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить перевозчика "${currentCargoCarrier?.name}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = cargoCarriers.find((o) => o.id == selectedRowKeys[0]);
            setCurrentCargoCarrier(entity);
        } else {
            setCurrentCargoCarrier(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ICargoCarrier> = [
            {
                title: 'Название',
                dataIndex: 'name',
                width: 600,
            },
            {
                title: 'ИНН',
                dataIndex: 'inn',
                width: 120,
            }, {
                title: 'Собственник',
                dataIndex: 'userFullName',
                width: 250,
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={cargoCarriers}
                loading={tableLoading}
                onRow={(record: ICargoCarrier) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default CargoCarriers;
